/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/**
 * Inventory Statuses Hook for ArendaPro+
 * 
 * Provides CRUD operations for inventory statuses using the new domain-specific table.
 * Replaces the generic tags system for inventory status management.
 * 
 * Following STYLEGUIDE.md patterns:
 * - Uses supabaseWorker for data operations
 * - Proper error handling and loading states
 * - React Query for caching and synchronization
 * - TypeScript strict typing
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useAuth } from '@/context/AuthContext';
import { 
  InventoryStatus, 
  InventoryStatusDb, 
  InventoryStatusUpdate,
  DomainStatusOption,
  DomainStatusFilters,
  DEFAULT_INVENTORY_STATUSES
} from '@/types/domain-statuses';
import {
  inventoryStatusDbToClient,
  inventoryStatusClientToDbInsert,
  inventoryStatusUpdateToDb,
  statusesToOptions,
  validateStatusName,
  validateColorCode,
  sanitizeStatusInput
} from '@/lib/mappers/domainStatusMappers';
import { toast } from 'sonner';

// =============================================================================
// QUERY KEYS
// =============================================================================

const INVENTORY_STATUSES_QUERY_KEY = 'inventoryStatuses';

export const inventoryStatusesKeys = {
  all: [INVENTORY_STATUSES_QUERY_KEY] as const,
  lists: () => [...inventoryStatusesKeys.all, 'list'] as const,
  list: (filters: DomainStatusFilters) => [...inventoryStatusesKeys.lists(), filters] as const,
  details: () => [...inventoryStatusesKeys.all, 'detail'] as const,
  detail: (id: string) => [...inventoryStatusesKeys.details(), id] as const,
};

// =============================================================================
// MAIN HOOK
// =============================================================================

export type UseInventoryStatusesOptions = {
  filters?: DomainStatusFilters;
  enabled?: boolean;
}

export function useInventoryStatuses(options: UseInventoryStatusesOptions = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { filters = {}, enabled = true } = options;

  // =============================================================================
  // QUERIES
  // =============================================================================

  const {
    data: inventoryStatuses = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: inventoryStatusesKeys.list(filters),
    queryFn: async (): Promise<InventoryStatus[]> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      let query = supabaseWorker
        .from('inventory_statuses')
        .select('*')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (filters.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching inventory statuses:', error);
        const errMsg = (error as { message?: string } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to fetch inventory statuses: ${errMsg}`);
      }

      const statuses = (data as InventoryStatusDb[]).map(inventoryStatusDbToClient);
      
      // FALLBACK: Если статусов нет, возвращаем дефолтные для предотвращения пустых селектов
      if (statuses.length === 0) {
        console.warn('[useInventoryStatuses] No statuses found, using fallback defaults');
        return DEFAULT_INVENTORY_STATUSES.map((name, index) => ({
          id: `fallback-${index}`,
          name,
          color: '#6b7280',
          description: `Статус инвентаря: ${name}`,
          sortOrder: index + 1,
          userId: user.id,
          createdAt: new Date(),
          updatedAt: new Date(),
        }));
      }
      
      return statuses;
    },
    enabled: enabled && !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // =============================================================================
  // MUTATIONS
  // =============================================================================

  const createMutation = useMutation({
    mutationFn: async (newStatus: Omit<InventoryStatus, 'id' | 'createdAt' | 'updatedAt' | 'userId'>): Promise<InventoryStatus> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Validate input
      const sanitized = sanitizeStatusInput(newStatus);
      
      if (!validateStatusName(sanitized.name || '')) {
        throw new Error('Название статуса инвентаря должно содержать от 1 до 100 символов');
      }

      if (sanitized.color && !validateColorCode(sanitized.color)) {
        throw new Error('Некорректный формат цвета. Используйте HEX формат (например, #10b981)');
      }

      // Check for duplicates
      const existingStatus = inventoryStatuses.find(
        status => status.name.toLowerCase() === sanitized.name?.toLowerCase()
      );
      
      if (existingStatus) {
        throw new Error('Статус инвентаря с таким названием уже существует');
      }

      const insertData = inventoryStatusClientToDbInsert({
        ...sanitized,
        userId: user.id,
        color: sanitized.color || '#10b981', // default color
      } as Omit<InventoryStatus, 'id' | 'createdAt' | 'updatedAt'> & { userId: string });

      const { data, error } = await supabaseWorker
        .from('inventory_statuses')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Error creating inventory status:', error);
        const errMsg = (error as { message?: string } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to create inventory status: ${errMsg}`);
      }

      return inventoryStatusDbToClient(data as InventoryStatusDb);
    },
    onSuccess: (newStatus) => {
      // Invalidate and refetch inventory statuses
      queryClient.invalidateQueries({ queryKey: inventoryStatusesKeys.all });
      toast.success(`Статус инвентаря "${newStatus.name}" создан`);
    },
    onError: (error: Error) => {
      console.error('Error creating inventory status:', error);
      toast.error(error.message || 'Ошибка при создании статуса инвентаря');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: InventoryStatusUpdate }): Promise<InventoryStatus> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Validate input
      const sanitized = sanitizeStatusInput(updates);

      if (sanitized.name && !validateStatusName(sanitized.name)) {
        throw new Error('Название статуса инвентаря должно содержать от 1 до 100 символов');
      }

      if (sanitized.color && !validateColorCode(sanitized.color)) {
        throw new Error('Некорректный формат цвета. Используйте HEX формат (например, #10b981)');
      }

      // Check for duplicates (excluding current status)
      if (sanitized.name) {
        const existingStatus = inventoryStatuses.find(
          status => status.id !== id && status.name.toLowerCase() === sanitized.name?.toLowerCase()
        );
        
        if (existingStatus) {
          throw new Error('Статус инвентаря с таким названием уже существует');
        }
      }

      const updateData = inventoryStatusUpdateToDb(sanitized);

      const { data, error } = await supabaseWorker
        .from('inventory_statuses')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating inventory status:', error);
        const errMsg = (error as { message?: string } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to update inventory status: ${errMsg}`);
      }

      if (!data) {
        throw new Error('Inventory status not found or access denied');
      }

      return inventoryStatusDbToClient(data as InventoryStatusDb);
    },
    onSuccess: (updatedStatus) => {
      // Invalidate and refetch inventory statuses
      queryClient.invalidateQueries({ queryKey: inventoryStatusesKeys.all });
      toast.success(`Статус инвентаря "${updatedStatus.name}" обновлен`);
    },
    onError: (error: Error) => {
      console.error('Error updating inventory status:', error);
      toast.error(error.message || 'Ошибка при обновлении статуса инвентаря');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabaseWorker
        .from('inventory_statuses')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting inventory status:', error);
        const errMsg = (error as { message?: string } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to delete inventory status: ${errMsg}`);
      }
    },
    onSuccess: (_, deletedId) => {
      // Find the deleted status name for toast
      const deletedStatus = inventoryStatuses.find(status => status.id === deletedId);
      
      // Invalidate and refetch inventory statuses
      queryClient.invalidateQueries({ queryKey: inventoryStatusesKeys.all });
      toast.success(`Статус инвентаря "${deletedStatus?.name || ''}" удален`);
    },
    onError: (error: Error) => {
      console.error('Error deleting inventory status:', error);
      toast.error(error.message || 'Ошибка при удалении статуса инвентаря');
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: Array<{ id: string; sortOrder: number }>): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Update each item's sort_order directly via Supabase
      const updates = items.map(item =>
        supabaseWorker
          .from('inventory_statuses')
          .update({ sort_order: item.sortOrder })
          .eq('id', item.id)
          .eq('user_id', user.id)
      );

      const results = await Promise.all(updates);
      
      // Check for errors
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        console.error('Error reordering inventory statuses:', errors);
        throw new Error('Failed to reorder some items');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryStatusesKeys.all });
      toast.success('Порядок обновлен');
    },
    onError: (error: Error) => {
      console.error('Error reordering inventory statuses:', error);
      toast.error(error.message || 'Ошибка при изменении порядка');
    },
  });

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  // КРИТИЧНО: Для инвентаря используем name как value, так как БД хранит строку, а не FK
  const statusOptions: DomainStatusOption[] = statusesToOptions(inventoryStatuses, true);

  const availableStatus = inventoryStatuses.find(status => 
    status.name.toLowerCase() === 'доступен' || 
    status.name.toLowerCase() === 'available'
  );

  const rentedStatus = inventoryStatuses.find(status => 
    status.name.toLowerCase() === 'в аренде' || 
    status.name.toLowerCase() === 'rented'
  );

  const maintenanceStatus = inventoryStatuses.find(status => 
    status.name.toLowerCase() === 'в ремонте' || 
    status.name.toLowerCase() === 'maintenance'
  );

  // =============================================================================
  // RETURN INTERFACE
  // =============================================================================

  return {
    // Data
    inventoryStatuses,
    options: statusOptions,
    availableStatus,
    rentedStatus,
    maintenanceStatus,
    
    // State
    isLoading,
    error,
    
    // Actions
    refetch,
    create: createMutation.mutate,
    update: updateMutation.mutate,
    delete: deleteMutation.mutate,
    reorder: reorderMutation.mutate,
    
    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isReordering: reorderMutation.isPending,
    
    // Mutation errors
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    reorderError: reorderMutation.error,
  };
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for getting inventory status options for selectors
 */
export function useInventoryStatusOptions(filters?: DomainStatusFilters) {
  const { options, isLoading, error } = useInventoryStatuses({ filters });
  
  return {
    options,
    isLoading,
    error,
  };
}

/**
 * Hook for getting a single inventory status by ID
 */
export function useInventoryStatus(id: string | undefined) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: inventoryStatusesKeys.detail(id || ''),
    queryFn: async (): Promise<InventoryStatus | null> => {
      if (!user?.id || !id) {
        return null;
      }

      const { data, error } = await supabaseWorker
        .from('inventory_statuses')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error) {
        const code = (error as { code?: string } | null)?.code;
        if (code === 'PGRST116') {
          return null; // Not found
        }
        const errMsg = (error as { message?: string } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to fetch inventory status: ${errMsg}`);
      }

      return inventoryStatusDbToClient(data as InventoryStatusDb);
    },
    enabled: !!user?.id && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
